import { Suspense, lazy } from 'react';
import LoadingPage from '../components/LoadingPage/LoadingPage';
import BrandShowroom from '../modules/brand-showroom/BrandShowroom';
import NotFoundPage from '../modules/errors/pages/NotFoundPage/NotFoundPage';
import ServerErrorPage from '../modules/errors/pages/ServerErrorPage/ServerErrorPage';
import { Login } from '../modules/login';
import { LoginContext } from '../modules/login/AuthContext';
import AuthFlow from '../modules/login/AuthFlow';
import AuthShell from '../modules/login/components/AuthShell';
import MyNetworkPublicSection from '../modules/my-network/MyNetworkPublicSection';
import OAuthConsentPage from '../modules/oauth/pages/OAuthConsentPage';
import OAuthLoginPage from '../modules/oauth/pages/OAuthLoginPage';
import OfflineWithoutDataPage from '../modules/offline/pages/OfflineWithoutDataPage/OfflineWithoutDataPage';
import PublicContractIndex from '../modules/orders/public-contract';
import OrderReadOnlyPage from '../modules/shared-entities/pages/OrderReadOnlyPage';
import SsoCallbackPage from '../modules/sso/pages/SsoCallbackPage';
import { whatsapp } from '../utils/constants';
import { AUTH_BASE_PATHS, AUTH_BRAND_PATHS } from './auth.routes';
import { PublicRouteProps } from './components/PublicRoute';
import withPublicNavigation from './navigations/withPublicNavigation';
import { useRootStyles } from './routes-page.styles';
import PATHS from './routes.const';

const CatalogProductsPublicScreen = lazy(
  () => import('../modules/catalogs/products/CatalogScreen/CatalogProductsPublicScreen'),
);

interface GetPublicRoutesParams {
  loggedin: boolean;
  isOnline: boolean;
  isForcingOfflineMode: boolean;
  rootStyles: ReturnType<typeof useRootStyles>;
  loginContext?: LoginContext;
}

interface RouteWithKey extends PublicRouteProps {
  key: string;
  usePublicRouteComponent: boolean;
}

const EnhancedBrandShowroom = withPublicNavigation(BrandShowroom);
const EnhancedCatalogProductsPublicScreen = withPublicNavigation(CatalogProductsPublicScreen);

const getPublicRoutes = ({
  isOnline,
  loggedin,
  isForcingOfflineMode,
  loginContext,
  rootStyles,
}: GetPublicRoutesParams) => {
  const offlinePublicRoutes: RouteWithKey[] = [
    {
      key: 'offline-page',
      usePublicRouteComponent: false,
      component: OfflineWithoutDataPage,
      path:
        isForcingOfflineMode && loggedin
          ? PATHS.offline.loggedNoData.path
          : PATHS.publicOffline.noData.path,
    },
  ];

  const publicRoutes: RouteWithKey[] = [
    {
      key: 'not-found',
      path: PATHS.public.notFound.path,
      component: NotFoundPage,
      usePublicRouteComponent: false,
    },
    {
      key: 'server-error',
      path: PATHS.public.serverError.path,
      component: ServerErrorPage,
      usePublicRouteComponent: false,
    },
    {
      key: 'oauth-login',
      path: PATHS.public.oauthLogin.path,
      component: OAuthLoginPage,
      disableRedirectWhenLogged: true,
      usePublicRouteComponent: true,
    },
    {
      key: 'oauth-consent',
      path: PATHS.public.oauthConsent.path,
      component: OAuthConsentPage,
      disableRedirectWhenLogged: true,
      usePublicRouteComponent: true,
    },
    {
      key: 'sso-callback',
      path: PATHS.public.ssoCallback.path,
      exact: true,
      component: SsoCallbackPage,
      disableRedirectWhenLogged: true,
      usePublicRouteComponent: true,
    },
    {
      key: 'auth',
      path: AUTH_BASE_PATHS,
      exact: true,
      component: AuthFlow,
      usePublicRouteComponent: true,
    },
    {
      key: 'sign-up',
      path: ['/cadastre-se', '/sign-up'],
      exact: true,
      render: () => {
        window.location.replace(
          `https://whatsa.me/${whatsapp.phone}/?t=ol%C3%A1!%20quero%20participar%20do%20Paris%20Fashion%20Week%20por%20teceo`,
        );
        return null;
      },
      usePublicRouteComponent: true,
    },
    {
      key: 'sign-contract',
      path: ['/assinar-contrato/:id', '/sign-contract/:id'],
      exact: true,
      component: PublicContractIndex,
      disableRedirectWhenLogged: true,
      usePublicRouteComponent: true,
    },
    {
      key: 'showroom',
      path: '/:slug/share/showroom',
      exact: true,
      render: () =>
        loginContext?.enablePublicShowroom ? (
          <EnhancedBrandShowroom brandId={loginContext?.id} />
        ) : (
          <AuthShell>
            <Login />
          </AuthShell>
        ),
      usePublicRouteComponent: true,
    },
    {
      exact: true,
      key: 'catalog',
      usePublicRouteComponent: true,
      path: '/:slug/share/catalog/:id',
      render: (props) => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <EnhancedCatalogProductsPublicScreen {...props} rootStyles={rootStyles} />
        </Suspense>
      ),
    },
    {
      key: 'order-suggestion',
      path: '/:slug/share/order/:id/suggestion',
      exact: true,
      component: withPublicNavigation(OrderReadOnlyPage),
      usePublicRouteComponent: true,
    },
  ];

  if (!loggedin) {
    publicRoutes.push({
      key: 'search-brands',
      path: '/connections/search-brands',
      exact: true,
      component: MyNetworkPublicSection,
      usePublicRouteComponent: true,
    });
  }

  if (!loggedin && loginContext) {
    publicRoutes.push(
      {
        key: 'brand-auth',
        path: AUTH_BRAND_PATHS,
        exact: true,
        component: AuthFlow,
        usePublicRouteComponent: true,
      },
      {
        key: 'brand-showroom',
        path: '/:slug',
        exact: true,
        render: () =>
          loginContext.enablePublicShowroom ? (
            <BrandShowroom brandId={loginContext?.id} />
          ) : (
            <AuthShell>
              <Login />
            </AuthShell>
          ),
        usePublicRouteComponent: true,
      },
    );
  }

  return isOnline ? publicRoutes : offlinePublicRoutes;
};

export default getPublicRoutes;
